import type {
  DashboardData,
  ProposalsResponse,
  SourceReliabilityResponse,
  SubagentsResponse,
  TeamMessagesResponse,
} from "./types";

type Subagent = SubagentsResponse["subagents"][number];
type TeamMessage = TeamMessagesResponse["messages"][number];
type Proposal = ProposalsResponse["proposals"][number];
type SourceRecord = SourceReliabilityResponse["sources"][number];

export interface MeetingSourceLink {
  label: string;
  url: string;
}

export interface MeetingChip {
  label: string;
  tone:
    | "token-btc"
    | "token-eth"
    | "token-sol"
    | "token-hype"
    | "token"
    | "news"
    | "source"
    | "evidence"
    | "cost"
    | "execution"
    | "status"
    | "authority"
    | "role";
}

type SpeakerIdentity = Subagent["identity"];

export interface MeetingItem {
  id: string;
  observedAt: string;
  speakerName: string;
  speakerRole: string;
  title: string;
  summary: string;
  bodyLines: string[];
  chips?: MeetingChip[];
  evidenceLine?: string;
  targetNames?: string[];
  sourceLinks?: MeetingSourceLink[];
  speakerIdentity?: SpeakerIdentity;
  symbol?: string; // coin symbol for badge in conversation
}

function roleForMission(mission = "") {
  const lower = mission.toLowerCase();
  if (
    lower.includes("market") ||
    lower.includes("rumor") ||
    lower.includes("source") ||
    lower.includes("시장")
  )
    return "시장 정보";
  if (
    lower.includes("execution") ||
    lower.includes("ledger") ||
    lower.includes("audit") ||
    lower.includes("체결")
  )
    return "실행 점검";
  if (lower.includes("risk")) return "위험 검토";
  if (lower.includes("research")) return "전략 조사";
  return "팀원";
}

function nameForAgent(agent?: Subagent) {
  return agent?.identity?.name?.trim() || (agent ? roleForMission(agent.mission) : undefined);
}

function agentByIdOrName(agents: Subagent[], value?: string) {
  if (!value) return undefined;
  const clean = value.toLowerCase();
  const parenthesized = clean.match(/\(([^)]+)\)/)?.[1]?.trim();
  return agents.find(
    (agent) =>
      agent.id.toLowerCase() === clean ||
      agent.identity?.name?.toLowerCase() === clean ||
      (parenthesized &&
        (agent.id.toLowerCase() === parenthesized ||
          agent.id.toLowerCase().startsWith(parenthesized) ||
          parenthesized.includes(agent.id.toLowerCase()))),
  );
}

function speakerForMessage(message: TeamMessage, agents: Subagent[]) {
  const agent = agentByIdOrName(agents, message.from);
  if (agent)
    return {
      name: nameForAgent(agent) ?? "팀원",
      role: roleForMission(agent.mission),
      identity: agent.identity,
    };
  if (message.from.toLowerCase().includes("ceo")) return { name: "👑 CEO", role: "" };
  if (message.from === "owner") return { name: "나", role: "오너" };
  if (message.from === "market-event-router") return { name: "시장 알림", role: "자동 감시" };
  if (message.from === "x-news-router") return { name: "X 소식 감시", role: "자동 감시" };
  if (message.from === "alert-ingest") return { name: "외부 소식 감시", role: "자동 감시" };
  return { name: message.from.replace(/\s*\([^)]*\)\s*/g, "").trim(), role: "팀" };
}

function targetNamesForMessage(message: TeamMessage, agents: Subagent[]) {
  if (/CEO 운영 점검|운영 점검/i.test(message.subject)) return [];
  const found = new Set<string>();
  const directTarget = agentByIdOrName(agents, message.to);
  if (directTarget) found.add(nameForAgent(directTarget) ?? directTarget.id);

  for (const agent of agents) {
    const name = nameForAgent(agent);
    if (name && (message.body.includes(name) || message.subject.includes(name))) found.add(name);
  }
  return [...found];
}

function compactWhitespace(value: string) {
  return value.replace(/\*\*/g, "").replace(/\s+/g, " ").trim();
}

function sentenceContaining(text: string, token: string) {
  const escaped = token.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return text.match(new RegExp(`[^.。\\n]*${escaped}[^.。\\n]*`, "i"))?.[0]?.trim();
}

function sanitizeLine(line: string) {
  return compactWhitespace(line)
    .replace(/(?:\.{3,}|…)/g, " ")
    .replace(/TradeIntent/g, "거래 요청")
    .replace(/Execution delegate/gi, "실행 담당")
    .replace(/Market intel/gi, "시장 정보")
    .replace(/rumor/gi, "소식")
    .replace(/소문/g, "소식")
    .replace(/subagent/gi, "직원")
    .replace(/source-reliability\.md/gi, "출처 메모")
    .replace(/get_market_snapshot/gi, "시세 확인")
    .replace(/\/Users\/love\/HyperMax[^ ]*/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

const ALERT_FRESHNESS_HOURS = 6;

function alertAgeHours(observedAt?: string, nowMs = Date.now()) {
  const observedMs = Date.parse(observedAt ?? "");
  if (!Number.isFinite(observedMs)) return undefined;
  return Math.max(0, (nowMs - observedMs) / 3_600_000);
}

function alertFreshnessLine(input: {
  observedAt?: string;
  ageHours?: number | null;
  freshness?: "fresh" | "stale";
  freshnessHours?: number;
}) {
  const freshnessHours = input.freshnessHours ?? ALERT_FRESHNESS_HOURS;
  const ageHours =
    typeof input.ageHours === "number" ? input.ageHours : alertAgeHours(input.observedAt);
  if (typeof ageHours !== "number") return `신선도 미확인 / 기준 ${freshnessHours}h`;
  const ageText = ageHours >= 24 ? `${(ageHours / 24).toFixed(1)}d` : `${ageHours.toFixed(1)}h`;
  const label = input.freshness ?? (ageHours <= freshnessHours ? "fresh" : "stale");
  return `신선도 ${label} / 경과 ${ageText} / 기준 ${freshnessHours}h`;
}

const SYMBOL_IGNORE = new Set([
  "CEO",
  "USDC",
  "USD",
  "USDT",
  "X",
  "TA",
  "ATH",
  "URL",
  "JSON",
  "ETF",
  "OI",
  "PNL",
  "ROE",
  "UTC",
  "RE",
  "NO",
  "TRADE",
  "WITHHOLD",
  "HOLD",
  "ENTER",
  "NONE",
  "API",
  "ACK",
  "ACCEPTED",
  "AGREE",
  "AGREED",
  "BLOCKED",
  "FULLY",
  "GOOD",
  "RECEIVED",
  "CHALLENGE",
  "CURRENT",
  "STATUS",
  "REPORT",
  "SUMMARY",
  "TICK",
  // Additional common false positives observed in reports, X scans, alerts, and logs (PR #80 follow-up Task 4)
  "FEE",
  "FEES",
  "BPS",
  "STANCE",
  "ID",
  "OK",
  "YES",
  "TRUE",
  "FALSE",
  "NULL",
  "PROGRESS",
  "LEDGER",
  "WATCHLIST",
  "REGIME",
  "CONF",
  "TRIGGER",
  "ASSETS",
  "PERPS",
  "SCAN",
  "RESULTS",
  "TIE",
  "VOLUME",
  "FUNDING",
  "INTEREST",
]);

function tokenTone(symbol: string): MeetingChip["tone"] {
  if (symbol === "BTC") return "token-btc";
  if (symbol === "ETH") return "token-eth";
  if (symbol === "SOL") return "token-sol";
  if (symbol === "HYPE") return "token-hype";
  return "token";
}

/**
 * Single internal helper for symbol extraction (deduplicates tokenChipsFor + symbolsFromReport).
 * Uses consistent permissive regex + ignore list + L\d+ filter. Called with appropriate limits.
 */
export function extractSymbols(text: string, limit = 5): string[] {
  if (!text) return [];
  return [...new Set(text.match(/\b[A-Z][A-Z0-9]{1,10}\b/g) ?? [])]
    .filter((symbol) => !SYMBOL_IGNORE.has(symbol) && !/^L\d+$/i.test(symbol))
    .slice(0, limit);
}

export function tokenChipsFor(text: string, limit = 3): MeetingChip[] {
  return extractSymbols(text, limit).map((symbol) => ({ label: symbol, tone: tokenTone(symbol) }));
}

function uniqueChips(chips: Array<MeetingChip | undefined>): MeetingChip[] {
  const seen = new Set<string>();
  return chips.filter((chip): chip is MeetingChip => {
    if (!chip) return false;
    const key = `${chip.tone}:${chip.label}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function hasFeeText(text: string) {
  return /\bfee|fees|수수료/i.test(text);
}

function hasExecutionText(text: string) {
  return /\bslippage|spread|liquidity|fill|execution|체결|유동성|오차/i.test(text);
}

function hasWeakMarketText(text: string) {
  return /weak market confirmation|시장 반응.*약|가격 반응.*약|no visible market|no price|no catalyst|원인 미확인|quiet regime|neutral regime/i.test(
    text,
  );
}

function hasEarlyNewsText(text: string) {
  return /early|fresh|recent|초기|rumor|소식/i.test(text);
}

function compactReasonText(text: string) {
  if (/zero concrete|no concrete|성과|failed|warning|경고/i.test(text)) {
    return "경고 뒤에도 구체적인 조건 제시가 없었습니다.";
  }
  if (/generic no-trade|generic caution|반복|repeated/i.test(text)) {
    const token = tokenChipsFor(text, 1)[0]?.label;
    return `같은 표현이 반복되어 ${token ? `${token} ` : ""}검토 조건을 더 분명히 맡겼습니다.`;
  }
  if (/tool|submit_trade_intent|권한|delegate|execution/i.test(text)) {
    return "실행 책임을 분명히 하기 위한 조정입니다.";
  }
  return shortManagementLine(text, 88);
}

function extractLabeledValue(body: string, label: string) {
  const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return body.match(new RegExp(`(?:^|\\n)${escaped}:\\s*([^\\n]+)`, "i"))?.[1]?.trim();
}

function cleanManagementValue(value = "") {
  return sanitizeLine(value)
    .replace(/직원-[0-9a-f-]{8,}/gi, "직원")
    .replace(/[0-9a-f]{8}-[0-9a-f-]{20,}/gi, "직원")
    .replace(/\((?:Mason|Ava)[^)]*\)/gi, "")
    .replace(/\bAssigned\b[^.。]*(?:[.。]|$)/gi, "")
    .replace(/\bNo revocation or reassignment needed\b[^.。]*(?:[.。]|$)/gi, "")
    .replace(/\bBoth\s+직원s\b/gi, "두 직원")
    .replace(/\b직원s\b/gi, "직원들")
    .replace(/\bno-trade stances?\b/gi, "무거래 판단")
    .replace(/\bzero X coverage\b/gi, "X 근거 없음")
    .replace(/\bsub-20bps move\b/gi, "작은 가격 움직임")
    .replace(/\bweak\/unknown market confirmation\b/gi, "가격 반응 불확실")
    .replace(/\bKeep roster unchanged\b[^.。]*(?:[.。]|$)/gi, "현재 인원 유지.")
    .replace(/\bConcrete\b[^.。]*(?:[.。]|$)/gi, "")
    .replace(/\bMonitor pending high-priority team messages\b/gi, "남은 중요 소식 확인")
    .replace(/\bHYPE 소식 scan\b/gi, "HYPE 소식")
    .replace(
      /\bno new delegation, challenge round, or hire required this tick\b/gi,
      "이번에는 권한 변경이나 신규 고용 없음",
    )
    .replace(/\bReconcile ledger\b[^.。]*(?:[.。]|$)/gi, "")
    .replace(/\bmarket confirmation\b/gi, "가격 반응")
    .replace(/\bdelegate\b/gi, "실행 담당")
    .replace(/\s*\.\.\.$/, "")
    .replace(/\s+/g, " ")
    .trim();
}

function shortManagementLine(value: string, maxLength = 92) {
  const clean = cleanManagementValue(value);
  void maxLength;
  return clean;
}

function linesForCeoManagementMessage(message: TeamMessage) {
  const action = cleanManagementValue(extractLabeledValue(message.body, "조직 행동") ?? "");
  const authority = cleanManagementValue(extractLabeledValue(message.body, "권한") ?? "");
  const management = cleanManagementValue(extractLabeledValue(message.body, "관리") ?? "");
  const next = cleanManagementValue(extractLabeledValue(message.body, "다음") ?? "");
  const lines: string[] = [];

  if (/고용 실행|신규 고용/.test(action)) {
    lines.push("새 직원을 고용했습니다.");
  } else if (/고용 조건/.test(action)) {
    lines.push("새 직원이 필요한지 검토했습니다.");
  } else if (/권한|실행 담당/.test(action) || authority) {
    lines.push("실행 담당과 권한 상태를 확인했습니다.");
  } else {
    lines.push("조직을 점검했고 이번에는 그대로 유지합니다.");
  }

  lines.push(
    management
      ? `직원 평가: ${shortManagementLine(management)}`
      : "직원 평가: 최근 업무 품질을 확인했습니다.",
  );
  lines.push(next ? `다음: ${shortManagementLine(next)}` : "다음: 새 소식이 오면 다시 판단합니다.");

  return lines.slice(0, 3);
}

interface MarketPair {
  symbol: string;
  price: number;
  changeBps?: number;
}

function marketMoveMarker(change?: number) {
  if (change === undefined || !Number.isFinite(change) || change === 0) return "";
  if (change > 0) return "▲";
  return "▼";
}

function priceText(price: number) {
  return price.toLocaleString("en-US", { maximumFractionDigits: 6 });
}

function parseMarketPairs(value: string) {
  const pairs: MarketPair[] = [];
  const source = value
    .replace(/^Watchlist mids:\s*/i, "")
    .replace(/^watchlist mids\s*/i, "")
    .replace(/\bno regime shift\b/gi, "")
    .replace(/\bneutral regime sustained\b/gi, "");
  const pattern =
    /\b([A-Z][A-Z0-9]{1,12}|k[A-Z0-9]{2,12})\s+(-?\d+(?:\.\d+)?)(?:\s*\(([-+]?\d+(?:\.\d+)?)\s*bps[^)]*\))?/gi;

  for (const match of source.matchAll(pattern)) {
    const symbol = match[1];
    const price = Number(match[2]);
    const changeBps = match[3] === undefined ? undefined : Number(match[3]);
    if (!Number.isFinite(price)) continue;
    pairs.push({ symbol, price, changeBps });
  }

  const priority = new Map(["BTC", "ETH", "SOL", "HYPE"].map((symbol, index) => [symbol, index]));
  return pairs
    .sort((left, right) => (priority.get(left.symbol) ?? 99) - (priority.get(right.symbol) ?? 99))
    .slice(0, 4);
}

function formatMarketPair(pair: MarketPair) {
  const marker = marketMoveMarker(pair.changeBps);
  const change = pair.changeBps === undefined ? "" : ` ${Math.abs(pair.changeBps).toFixed(1)}bp`;
  return `${marker ? `${marker} ` : ""}${pair.symbol} ${priceText(pair.price)}${change}`;
}

function cleanWatchlist(value: string) {
  const pairs = parseMarketPairs(value);
  if (pairs.length) return pairs.map(formatMarketPair).join(" · ");

  return value
    .replace(/^Watchlist mids:\s*/i, "")
    .replace(/^watchlist mids\s*/i, "")
    .replace(/\bno regime shift\b/gi, "")
    .split(/[,/;]/)
    .map((part) => part.replace(/\s*\([^)]*\)/g, "").trim())
    .filter(Boolean)
    .slice(0, 4)
    .join(" · ");
}

function sourceLabel(source: string) {
  try {
    const url = new URL(source);
    if (url.hostname.includes("x.com") && /\/status\//.test(url.pathname)) return "원문";
    return url.hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
}

function isExactPostSource(source: string) {
  try {
    const url = new URL(source);
    return url.hostname.includes("x.com") && /\/status\//.test(url.pathname);
  } catch {
    return false;
  }
}

function pushSourceLink(map: Map<string, MeetingSourceLink>, source: string) {
  const clean = source.trim().replace(/[).,;]+$/g, "");
  if (!clean) return;
  if (/Users|HyperMax|Obsidian|source-reliability|cycle|^\s*~|time validity/i.test(clean)) return;
  if (["love", "root", "local", "localhost"].includes(clean.toLowerCase())) return;
  if (!/^https?:\/\//i.test(clean)) return;
  if (clean.includes("x.com") && !isExactPostSource(clean)) return;
  const label = sourceLabel(clean);
  if (!label || /unknown/i.test(label)) return;
  map.set(clean, { label, url: clean });
}

function sourcesFromText(text: string) {
  const found: string[] = [];
  for (const match of text.matchAll(/https?:\/\/[^\s)"'\]}]+/gi)) found.push(match[0]);
  return found;
}

function primaryAssetFromText(text: string) {
  const subjectAsset = text
    .match(/(?:판단 요청|검토)[^:]*:\s*([A-Z0-9]{2,12})/i)?.[1]
    ?.toUpperCase();
  if (subjectAsset && !["ETF", "ATH", "CEO"].includes(subjectAsset)) return subjectAsset;
  const listed = text
    .match(/Assets:\s*([A-Z0-9,\s]+)/i)?.[1]
    ?.split(",")
    .map((asset) => asset.trim())
    .find((asset) => /^[A-Z0-9]{2,12}$/.test(asset));
  if (listed) return listed;
  const major = text.match(/\b(HYPE|BTC|ETH|SOL)\b/i)?.[1]?.toUpperCase();
  if (major) return major;
  return tokenChipsFor(text, 1)[0]?.label ?? "해당 코인";
}

function stanceForMessageBody(body: string) {
  return body.match(/Stance:\s*([a-z-]+)/i)?.[1]?.toLowerCase();
}

function isEmployeeJudgmentRequest(text: string) {
  return /No CEO trade decision|CEO trade decision|employee judgment|직원 판단/i.test(text);
}

function sourceLinksFor(text: string, _sources: SourceRecord[]) {
  const links = new Map<string, MeetingSourceLink>();
  for (const source of sourcesFromText(text)) pushSourceLink(links, source);

  return [...links.values()].slice(0, 1);
}

function titleForMessage(message: TeamMessage) {
  const combined = `${message.subject} ${message.body}`;
  const asset = primaryAssetFromText(combined);
  const stance = stanceForMessageBody(message.body);
  if (message.from === "market-event-router") return "가격 움직임";
  if (message.from === "x-news-router") return "소식";
  if (message.from === "alert-ingest" && asset !== "해당 코인") return `${asset} 외부 소식`;
  if (message.from === "alert-ingest") return "외부 소식";
  if (/CEO 운영 점검|운영 점검/i.test(message.subject)) return "운영 점검";
  if (stance === "no-trade") return `${asset} 판단`;
  if (stance === "propose-trade" || stance === "execute") return `${asset} 거래 제안`;
  if (isEmployeeJudgmentRequest(combined)) return `${asset} 거래 검토 배정`;
  if (/X 소식 판단 요청|rumor|소식/i.test(combined) && /HYPE|BTC|ETH|SOL/i.test(combined))
    return `${asset} 가격 반응 확인`;
  if (message.from === "owner") return "오너 메시지";
  if (/^RE:/i.test(message.subject) && asset !== "해당 코인") return `${asset} 검토`;
  if (
    asset !== "해당 코인" &&
    /closed|confirmed|status|routing|entry|vote|acknowledg/i.test(message.subject)
  )
    return `${asset} 검토`;
  return sanitizeLine(message.subject).slice(0, 34) || "회의 메시지";
}

function jsonAfterLabel(text: string, label: string) {
  const index = text.indexOf(label);
  if (index < 0) return undefined;
  const raw = text.slice(index + label.length).trim();
  try {
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
}

function linesForMarketRouterMessage(message: TeamMessage) {
  const payload = jsonAfterLabel(message.body, "이벤트 요약:");
  const events = Array.isArray(payload) ? payload : [];
  const lines = events.slice(0, 4).map((event) => {
    const symbol = event.symbol ?? event.channel ?? "시장";
    const price = Number(event.price);
    const previous = Number(event.previous);
    const change =
      Number.isFinite(price) && Number.isFinite(previous) ? price - previous : undefined;
    const direction = change === undefined || change >= 0 ? "상승" : "하락";
    const marker = marketMoveMarker(change);
    const bps = Number.isFinite(Number(event.moveBps))
      ? `${Number(event.moveBps).toFixed(2)}bp`
      : "변동";
    return `${marker ? `${marker} ` : ""}${symbol} ${bps} ${direction}`;
  });
  if (!lines.length) lines.push("시장 움직임 감지");
  return lines;
}

function linesForNewsRouterMessage(message: TeamMessage) {
  const firstJson = message.body.indexOf("{");
  let payload: any;
  if (firstJson >= 0) {
    try {
      payload = JSON.parse(message.body.slice(firstJson).trim());
    } catch {
      payload = undefined;
    }
  }
  const findings = Array.isArray(payload?.findings) ? payload.findings : [];
  const firstFinding = findings[0];
  const headline = payload?.headline || firstFinding?.summary;
  const topic = firstFinding?.topic || headline;
  const asset = primaryAssetFromText(`${message.subject} ${JSON.stringify(payload ?? {})}`);
  const weakMarket = /weak|unknown|none|약/i.test(String(firstFinding?.marketConfirmation ?? ""));
  if (topic && asset !== "해당 코인") {
    const shortTopic = sanitizeLine(String(topic)).replace(new RegExp(`^${asset}\\s*`, "i"), "");
    return [
      `${asset}${shortTopic ? ` ${shortTopic}` : ""} 이야기가 X에서 돌고 있어요.${
        weakMarket ? " 아직 시장 반응은 약합니다." : ""
      }`.trim(),
    ];
  }
  const line = headline ? `X news: ${headline}` : "X news: 새 소식 감지";
  const clean = sanitizeLine(line);
  return [clean];
}

function evidenceLineForNewsMessage(message: TeamMessage) {
  const firstJson = message.body.indexOf("{");
  if (firstJson < 0) return undefined;
  let payload: any;
  try {
    payload = JSON.parse(message.body.slice(firstJson).trim());
  } catch {
    return undefined;
  }
  const firstFinding = Array.isArray(payload?.findings) ? payload.findings[0] : undefined;
  if (!firstFinding) return undefined;
  const topic = String(firstFinding.topic ?? payload.headline ?? "").toLowerCase();
  const weakMarket = /weak|unknown|none|약/i.test(String(firstFinding.marketConfirmation ?? ""));
  const story = topic.includes("etf")
    ? "ETF 유입 이야기는 보이지만"
    : topic.includes("whale") || topic.includes("고래")
      ? "고래 움직임 이야기는 보이지만"
      : "관련 글은 보이지만";
  if (weakMarket) return `근거: ${story}, 바로 따라붙는 가격 움직임은 약합니다.`;
  return `근거: ${story}, 실제 거래로 이어질지는 더 확인이 필요합니다.`;
}

function chipsForNewsMessage(message: TeamMessage) {
  const sourceCount = sourceLinksFor(`${message.subject}\n${message.body}`, []).length;
  return uniqueChips([
    ...tokenChipsFor(`${message.subject} ${message.body}`, 2),
    { label: "X 소식", tone: "news" },
    sourceCount ? { label: `원문 ${sourceCount}개`, tone: "source" } : undefined,
    /weak|unknown|none|시장 반응.*약|가격 반응.*약/i.test(message.body)
      ? { label: "시장 반응 약함", tone: "evidence" }
      : undefined,
  ]);
}

function noTradeReasons(body: string) {
  const reasons: string[] = [];
  if (/weak market confirmation|시장 반응.*약|가격 반응.*약|no visible market|no price/i.test(body))
    reasons.push("가격 반응이 약함");
  if (/quiet regime|neutral regime|stable mids|no regime shift/i.test(body))
    reasons.push("장이 조용함");
  if (/\bOI\b|open interest/i.test(body))
    reasons.push(
      /no|weak|unknown|부족/i.test(body) ? "따라붙는 거래 움직임이 약함" : "거래 움직임 확인 필요",
    );
  if (/early|fresh|recent|초기/i.test(body)) reasons.push("소식이 아직 초기 단계");
  if (/source|confirmation|independent|catalyst/i.test(body))
    reasons.push("확인된 소식이 아직 적음");
  return [...new Set(reasons)].slice(0, 3);
}

function evidenceLineForTradingText(body: string, asset: string, stance?: string) {
  if (hasFeeText(body) && hasExecutionText(body) && hasWeakMarketText(body)) {
    return "근거: 소식은 있지만 가격 움직임이 약하고, 비용을 넘길 여유가 작습니다.";
  }
  if (hasFeeText(body) && hasExecutionText(body)) {
    return "근거: 수수료와 체결 오차를 감안하면 지금은 여유가 작습니다.";
  }
  if (hasWeakMarketText(body) && hasEarlyNewsText(body)) {
    return "근거: 소식은 있지만 바로 따라붙는 시장 움직임이 약합니다.";
  }
  if (hasWeakMarketText(body)) {
    return "근거: 가격 움직임만으로는 아직 거래할 이유가 부족합니다.";
  }
  if (stance === "no-trade") {
    return `근거: ${asset}을 거래할 만큼 분명한 조건이 아직 보이지 않습니다.`;
  }
  return undefined;
}

function chipsForTradingText(body: string, asset: string, stance?: string) {
  return uniqueChips([
    ...tokenChipsFor(asset),
    stance === "no-trade" || stance === "withhold" ? { label: "보류", tone: "status" } : undefined,
    stance === "propose-trade" || stance === "execute"
      ? { label: "거래 검토", tone: "status" }
      : undefined,
    hasFeeText(body) ? { label: "수수료 확인", tone: "cost" } : undefined,
    hasExecutionText(body) ? { label: "체결 주의", tone: "execution" } : undefined,
    hasWeakMarketText(body) ? { label: "시장 반응 약함", tone: "evidence" } : undefined,
  ]);
}

function naturalNoTradeLine(asset: string, body: string) {
  if (hasFeeText(body) || hasExecutionText(body)) {
    return `${asset}은 지금 들어가기엔 움직임과 근거가 아직 약해 보여요.`;
  }
  const reasons = noTradeReasons(body);
  return `${asset}은 지금은 보류가 낫습니다. ${
    reasons.length
      ? `이유는 ${reasons.join(", ")}입니다.`
      : "거래할 만큼 분명한 근거가 아직 부족합니다."
  }`;
}

function naturalPeerReplyLine(body: string) {
  if (
    /good challenge|좋은 지적/i.test(body) &&
    /time\+price|time and price|가격과 시간/i.test(body)
  ) {
    return "좋은 지적이에요. 가격과 시간을 같이 보겠습니다.";
  }
  if (/good challenge|좋은 지적/i.test(body)) return "좋은 지적이에요. 조건을 다시 보겠습니다.";
  if (/agree|accepted|acknowledged|동의|수락/i.test(body))
    return "동의합니다. 조건을 반영하겠습니다.";
  if (/disagree|challenge|반박/i.test(body)) return "그 부분은 다시 따져보겠습니다.";
  return undefined;
}

function cleanFallbackLines(body: string) {
  return body
    .split(/(?<=\.)\s+/)
    .map((line) =>
      sanitizeLine(line)
        .replace(/^stance:\s*/i, "")
        .replace(/\bAGREE\b|\bACCEPTED\b|\bACK\b/gi, "")
        .replace(/\s+/g, " ")
        .trim(),
    )
    .filter(Boolean)
    .slice(0, 2);
}

function linesForMessage(message: TeamMessage, targetNames: string[]) {
  if (message.from === "market-event-router") return linesForMarketRouterMessage(message);
  if (message.from === "x-news-router") return linesForNewsRouterMessage(message);
  if (/CEO 운영 점검|운영 점검/i.test(message.subject)) {
    return linesForCeoManagementMessage(message);
  }

  const body = compactWhitespace(message.body);
  const lines: string[] = [];
  const stance = stanceForMessageBody(body);
  const asset = primaryAssetFromText(`${message.subject} ${body}`);
  const hasNews = /HYPE|BTC|ETH|SOL/i.test(body);
  const needsEmployeeJudgment = isEmployeeJudgmentRequest(body);
  if (needsEmployeeJudgment && targetNames.length) {
    lines.push(`${targetNames.join(", ")}에게 ${asset} 검토를 맡겼습니다.`);
  } else if (needsEmployeeJudgment) {
    lines.push(`팀에 ${asset} 검토를 맡겼습니다.`);
  }
  if (stance === "no-trade") {
    lines.push(naturalNoTradeLine(asset, body));
  }
  if (stance === "propose-trade")
    lines.push(`${asset} 거래 제안으로 올려볼게요. 실행 조건을 더 좁히겠습니다.`);
  if (stance === "execute")
    lines.push(`${asset} 실행 조건을 확인하겠습니다. 체결 가능성과 비용을 같이 볼게요.`);
  const peerReply = naturalPeerReplyLine(body);
  if (peerReply) lines.push(peerReply);
  if (hasNews && !needsEmployeeJudgment && stance !== "no-trade")
    lines.push(`${asset} 가격 반응을 확인하고 있습니다.`);
  if (/quiet regime|weak market confirmation|시장 반응|Market:/i.test(body))
    lines.push("가격 반응은 아직 약해서 조금 더 확인이 필요합니다.");

  if (!lines.length) {
    lines.push(...cleanFallbackLines(body));
  }

  return [...new Set(lines.map(sanitizeLine).filter(Boolean))].slice(0, 5);
}

function titleForReport(agent: Subagent) {
  const role = roleForMission(agent.mission);
  if (role === "시장 정보") return "소식 확인";
  if (role === "실행 점검") return "체결 기록 점검";
  return "업무 보고";
}

export function symbolsFromReport(text: string) {
  // Delegates to shared extractSymbols (hardened + deduped); limit 5 preserved for report use.
  return extractSymbols(text, 5);
}

function linesForXSearchReport(clean: string) {
  if (!/x_search|X search|Ran x_search|catalyst|원인 미확인|소식/i.test(clean)) return [];
  const symbols = symbolsFromReport(clean);
  const target = symbols.length ? symbols.join(", ") : "시장 움직임";
  const lines = [`${target} 관련 X 소식을 확인했습니다.`];
  if (/원인 미확인|None tie|no exact|no catalyst|not tie/i.test(clean)) {
    lines.push("직접 연결되는 원인은 아직 확인되지 않았습니다.");
  } else if (/Binance|ETF|listing|hack|exploit|whale|고래|청산|유입|상장|해킹/i.test(clean)) {
    lines.push("가격에 영향을 줄 수 있는 소식만 원문으로 남겼습니다.");
  }
  return lines;
}

function linesForReport(agent: Subagent, summary: string) {
  const beforeArtifacts =
    summary.split(/Artifacts touched\/inspected|Artifacts|Next actions/i)[0] ?? summary;
  const clean = compactWhitespace(beforeArtifacts)
    .replace(/Progress Report\s*-\s*[^:]+/gi, "")
    .replace(/\bSummary:?\b/gi, "")
    .trim();
  const lines: string[] = [];
  const watchlist =
    clean
      .match(
        /watchlist mids:?\s*(.*?)(?:\.\s*(?:Source reliability|get_source_reliability|No new|Neutral regime|Wiki)|$)/i,
      )?.[1]
      ?.trim() ?? sentenceContaining(clean, "Watchlist mids");
  const sourceCount = clean.match(/Source reliability refreshed:\s*([0-9]+ sources?)/i)?.[1];
  const pnl = clean.match(/Total closed PnL:\s*(-?\d+(?:\.\d+)?)/i)?.[1];
  const fees = clean.match(/Total fees:\s*(-?\d+(?:\.\d+)?)/i)?.[1];
  const role = roleForMission(agent.mission);

  if (role === "시장 정보") lines.push(...linesForXSearchReport(clean));

  if (watchlist) {
    const compactWatchlist = cleanWatchlist(watchlist);
    lines.push(sanitizeLine(`주요 가격: ${compactWatchlist}`));
  }
  if (sourceCount)
    lines.push(`출처 ${sourceCount.replace(/\s*sources?/i, "개")}를 새로 확인했어요.`);
  if (/No new contradictions/i.test(clean)) lines.push("서로 충돌하는 새 근거는 아직 없어요.");
  if (pnl || fees)
    lines.push(
      `체결 기록 확인했어요${pnl ? `: 손익 ${pnl}` : ""}${fees ? `, 수수료 ${fees}` : ""}.`,
    );

  if (!lines.length) {
    lines.push(
      ...clean
        .split(/(?<=\.)\s+/)
        .map(sanitizeLine)
        .filter(Boolean)
        .slice(0, 4),
    );
  }

  return [...new Set(lines.map(sanitizeLine).filter(Boolean))].slice(0, 5);
}

function titleForProposal(proposal: Proposal) {
  if (proposal.proposalType === "trade") return "거래 제안";
  if (proposal.proposalType === "authority") return "권한 제안";
  if (proposal.proposalType === "hire" || proposal.proposalType === "terminate") return "조직 제안";
  if (proposal.proposalType === "process") return "업무 제안";
  return "직원 제안";
}

function decisionLabel(decision?: Proposal["decision"]) {
  if (!decision) return undefined;
  if (decision.decision === "approved") return "승인";
  if (decision.decision === "rejected") return "반려";
  return "종료";
}

function linesForProposal(proposal: Proposal) {
  const lines = [
    proposal.thesis ? `판단: ${proposal.thesis}` : "",
    proposal.requestedAction ? `요청: ${proposal.requestedAction}` : "",
    proposal.knownRisks ? `주의: ${proposal.knownRisks}` : "",
  ];
  const label = decisionLabel(proposal.decision);
  if (label && proposal.decision) lines.push(`대표 결정: ${label}. ${proposal.decision.reason}`);
  return [...new Set(lines.map(sanitizeLine).filter(Boolean))].slice(0, 5);
}

function modelLabel(provider?: string, model?: string) {
  if (provider && model) return `${provider}/${model}`;
  return model ?? provider ?? "";
}

function runtimePhase2Line(delegate: DashboardData["executionDelegate"]) {
  const runtime = delegate?.runtimePhase2ModelConfig;
  const baseline = modelLabel(runtime?.provider, runtime?.model);
  if (!baseline) return "";
  const experiment = modelLabel(runtime?.experimentProvider, runtime?.experimentModel);
  const rate =
    typeof runtime?.experimentRate === "number" && runtime.experimentRate > 0
      ? `, 실험 ${experiment} ${Math.round(runtime.experimentRate * 100)}%`
      : "";
  return `실제 Phase2: ${baseline}${experiment ? rate : ""}`;
}

function managementItemsForAgents(
  agents: Subagent[],
  delegate: DashboardData["executionDelegate"],
): MeetingItem[] {
  const items: MeetingItem[] = [];
  for (const agent of agents) {
    const name = nameForAgent(agent) ?? "직원";
    if (agent.status === "active" && agent.createdAt && /ceo/i.test(agent.createdBy ?? "")) {
      const role = roleForMission(agent.mission);
      const summary = `${name}를 ${role} 담당으로 고용했습니다.`;
      // Extract once (Task 4.3)
      const hireText = agent.mission;
      const hireChips = tokenChipsFor(hireText, 3);
      items.push({
        id: `${agent.id}-hired-${agent.createdAt}`,
        observedAt: agent.createdAt,
        speakerName: "👑 CEO",
        speakerRole: "",
        title: "직원 고용",
        summary,
        bodyLines: [summary],
        chips: uniqueChips([
          { label: role, tone: "role" },
          ...hireChips,
          /x|소식|rumor/i.test(hireText) ? { label: "X 소식", tone: "news" } : undefined,
        ]),
        speakerIdentity: agent.identity,
        symbol: hireChips[0]?.label,
      });
    }
    for (const redirection of agent.redirections ?? []) {
      const summary = `${name}의 임무를 조정했습니다.`;
      const evidenceLine = redirection.reason
        ? `근거: ${compactReasonText(redirection.reason)}`
        : undefined;
      // Extract once per redirect to avoid dup extraction calls (Task 4.3 scope-a)
      const redirectText = `${redirection.mission} ${redirection.reason ?? ""}`.trim();
      const redirectChips = tokenChipsFor(redirectText, 2);
      items.push({
        id: `${agent.id}-redirect-${redirection.redirectedAt}`,
        observedAt: redirection.redirectedAt,
        speakerName: "👑 CEO",
        speakerRole: "",
        title: "임무 조정",
        summary,
        bodyLines: [summary],
        chips: uniqueChips([
          { label: roleForMission(redirection.mission), tone: "role" },
          ...redirectChips,
        ]),
        evidenceLine,
        speakerIdentity: agent.identity,
        symbol: redirectChips[0]?.label,
      });
    }
    if (agent.status === "terminated" && agent.terminatedReason && agent.lastActivityAt) {
      const summary = `${name}와의 계약을 종료했습니다.`;
      const evidenceLine = `근거: ${compactReasonText(agent.terminatedReason)}`;
      items.push({
        id: `${agent.id}-terminated-${agent.lastActivityAt}`,
        observedAt: agent.lastActivityAt,
        speakerName: "👑 CEO",
        speakerRole: "",
        title: "직원 정리",
        summary,
        bodyLines: [summary],
        chips: [{ label: "업무 종료", tone: "status" }],
        evidenceLine,
        speakerIdentity: agent.identity,
        // terminated 항목은 코인 관련성이 낮아 symbol 생략
      });
    }
  }

  const activeDelegate = delegate?.executionDelegate
    ? agentByIdOrName(agents, delegate.executionDelegate.subagentId)
    : undefined;
  if (delegate?.executionDelegate && activeDelegate) {
    const name = nameForAgent(activeDelegate) ?? "실행 담당";
    const bodyLines = [`${name}에게 실행 권한을 맡겼습니다.`, runtimePhase2Line(delegate)]
      .map(sanitizeLine)
      .filter(Boolean);
    // Extract once for delegate reason (Task 4.3)
    const delegateReason = delegate.executionDelegate.reason ?? "";
    const delegateChips = tokenChipsFor(delegateReason, 2);
    items.push({
      id: `delegate-${delegate.executionDelegate.subagentId}-${delegate.executionDelegate.assignedAt}`,
      observedAt: delegate.executionDelegate.assignedAt,
      speakerName: "👑 CEO",
      speakerRole: "",
      title: "권한 배정",
      summary: bodyLines[0],
      bodyLines,
      chips: uniqueChips([{ label: "실행 권한", tone: "authority" }, ...delegateChips]),
      evidenceLine: delegateReason ? `근거: ${compactReasonText(delegateReason)}` : undefined,
      speakerIdentity: activeDelegate.identity,
      symbol: delegateChips[0]?.label,
    });
  }

  return items;
}

function isRoutineReportItem(item: MeetingItem) {
  if (item.speakerRole === "실행 점검") return true;
  const joined = item.bodyLines.join(" ");
  if (
    item.speakerRole === "시장 정보" &&
    /^주요 가격:/.test(item.summary) &&
    !/X|ETF|whale|고래|청산|유입|상장|해킹|소식/i.test(joined)
  )
    return true;
  // Hide provider-empty-response noise from the meeting room
  if (/Runtime warning:.*exited successfully but produced no useful output/i.test(item.summary))
    return true;
  return false;
}

function dedupeKeyForItem(item: MeetingItem) {
  if (item.title.endsWith("거래 검토 배정")) return `${item.title}:${item.speakerName}`;
  if (item.title.endsWith("판단")) return `${item.title}:${item.summary}`;
  if (item.title.endsWith("가격 반응 확인")) return `${item.title}:${item.summary}`;
  if (item.speakerRole === "자동 감시") return `${item.title}:${item.summary}`;
  return "";
}

function dedupeMeetingItems(items: MeetingItem[]) {
  const seen = new Set<string>();
  return items.filter((item) => {
    const key = dedupeKeyForItem(item);
    if (!key) return true;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function collabExecutionLine(
  note: NonNullable<DashboardData["collaborationNotes"]>["notes"][number],
  ledgerExecutionByIntentId: Map<
    string,
    NonNullable<DashboardData["ledger"]>["recentExecutions"][number]
  >,
) {
  if (!note.executionResult?.status) return "";
  const exec = note.executionResult;
  const ledgerExecution = exec.intentId ? ledgerExecutionByIntentId.get(exec.intentId) : undefined;
  const status = ledgerExecution?.status ?? exec.status;
  const intent = exec.intentId ? ` (${exec.intentId})` : "";
  const staleNoteStatus =
    ledgerExecution?.status && ledgerExecution.status !== exec.status
      ? ` / 노트 기록 ${exec.status}`
      : "";
  return `실행 결과: ${status}${intent}${staleNoteStatus}`;
}

export function buildMeetingItems(data: DashboardData, limit = 12): MeetingItem[] {
  const agents = data.subagents?.subagents ?? [];
  const messages = data.teamMessages?.messages ?? [];
  const proposals = data.proposals?.proposals ?? [];
  const sources = data.sources?.sources ?? [];
  const alertCandidates = data.alertCandidates?.alerts ?? [];
  const managementItems = managementItemsForAgents(agents, data.executionDelegate);
  const messageItems = messages.map((message) => {
    const speaker = speakerForMessage(message, agents);
    const targetNames = targetNamesForMessage(message, agents);
    const bodyLines = linesForMessage(message, targetNames);
    const sourceLinks = sourceLinksFor(`${message.subject}\n${message.body}`, sources);
    const body = compactWhitespace(message.body);
    const asset = primaryAssetFromText(`${message.subject} ${body}`);
    const stance = stanceForMessageBody(body);
    const isNewsMessage = message.from === "x-news-router";
    const chips = isNewsMessage
      ? chipsForNewsMessage(message)
      : message.from === "market-event-router" || /CEO 운영 점검|운영 점검/i.test(message.subject)
        ? undefined
        : chipsForTradingText(body, asset, stance);
    const evidenceLine = isNewsMessage
      ? evidenceLineForNewsMessage(message)
      : message.from === "market-event-router" || /CEO 운영 점검|운영 점검/i.test(message.subject)
        ? undefined
        : evidenceLineForTradingText(body, asset, stance);
    return {
      id: message.id,
      observedAt: message.createdAt,
      speakerName: speaker.name,
      speakerRole: speaker.role,
      speakerIdentity: speaker.identity,
      title: titleForMessage(message),
      summary: bodyLines[0] ?? "회의 메시지가 도착했습니다.",
      bodyLines,
      chips,
      evidenceLine,
      targetNames,
      sourceLinks,
      // 메시지 내용에서 추론한 코인 심볼 — 회의실 뱃지에 표시
      symbol: asset !== "해당 코인" ? asset : undefined,
    };
  });

  const reportItems = agents.flatMap((agent) => {
    const report = agent.reports?.at(-1);
    if (!report) return [];
    const bodyLines = linesForReport(agent, report.summary);
    const role = roleForMission(agent.mission);
    const sourceLinks = role === "시장 정보" ? sourceLinksFor(report.summary, sources) : [];
    // Extract once (local var) to avoid duplicate scan for chips vs symbol (Task 4.3)
    const reportText = report.summary;
    const reportSyms = symbolsFromReport(reportText);
    const item = {
      id: `${agent.id}-${report.reportedAt}`,
      observedAt: report.reportedAt,
      speakerName: nameForAgent(agent) ?? "팀원",
      speakerRole: role,
      speakerIdentity: agent.identity,
      title: titleForReport(agent),
      summary: bodyLines[0] ?? "업무 보고가 도착했습니다.",
      bodyLines,
      chips: uniqueChips([
        ...tokenChipsFor(reportText, 3),
        role === "시장 정보" && /x_search|X search|소식/i.test(reportText)
          ? { label: "X 소식", tone: "news" }
          : undefined,
        hasFeeText(reportText) ? { label: "수수료 확인", tone: "cost" } : undefined,
        hasExecutionText(reportText) ? { label: "체결 주의", tone: "execution" } : undefined,
      ]),
      evidenceLine: evidenceLineForTradingText(reportText, primaryAssetFromText(reportText)),
      sourceLinks,
      // 보고 내용에서 추출한 첫 번째 코인 심볼
      symbol: reportSyms[0],
    };
    return isRoutineReportItem(item) ? [] : [item];
  });

  const proposalItems = proposals.map((proposal) => {
    const agent = agentByIdOrName(agents, proposal.proposerId);
    const bodyLines = linesForProposal(proposal);
    // Extract once (local var) to avoid duplicate symbol scan in chips + symbol field (Task 4.3)
    const proposalText = `${proposal.thesis ?? ""} ${proposal.requestedAction ?? ""}`.trim();
    const proposalChips = tokenChipsFor(proposalText, 3);
    return {
      id: proposal.id,
      observedAt: proposal.decision?.decidedAt ?? proposal.createdAt,
      speakerName: nameForAgent(agent) ?? proposal.proposerId,
      speakerRole: agent ? roleForMission(agent.mission) : "직원",
      speakerIdentity: agent?.identity,
      title: titleForProposal(proposal),
      summary: bodyLines[0] ?? "직원 제안이 올라왔습니다.",
      bodyLines,
      chips: uniqueChips([
        ...proposalChips,
        proposal.proposalType === "trade" ? { label: "거래 검토", tone: "status" } : undefined,
        proposal.proposalType === "hire" || proposal.proposalType === "terminate"
          ? { label: "조직", tone: "role" }
          : undefined,
      ]),
      evidenceLine: proposal.knownRisks
        ? `근거: ${sanitizeLine(proposal.knownRisks).slice(0, 92)}`
        : undefined,
      symbol: proposalChips[0]?.label,
    };
  });

  const alertItems = alertCandidates.map((alert) => {
    const text = alert.normalizedText ?? alert.rawTextRedacted ?? "";
    const assets = alert.assets?.length ? alert.assets.join(", ") : "자산 미지정";
    const sourceQuality = alert.sourceClass ? ` / 품질 ${alert.sourceClass}` : "";
    const bodyLines = [
      `${alert.sourceName ?? alert.sourceId} / 전송 ${alert.sourceType}${sourceQuality} / score ${alert.candidateScore}`,
      alertFreshnessLine({
        observedAt: alert.observedAt ?? alert.receivedAt,
        ageHours: alert.ageHours,
        freshness: alert.freshness,
        freshnessHours: data.alertCandidates?.freshnessHours,
      }),
      `${assets} ${alert.catalystType} ${alert.urgency}`,
      text,
    ].filter(Boolean);
    return {
      id: alert.id,
      observedAt: alert.observedAt ?? alert.receivedAt,
      speakerName: "외부 소식 감시",
      speakerRole: "자동 감시",
      title: "외부 소식 후보",
      summary: bodyLines[0] ?? "새 외부 소식 후보가 들어왔습니다.",
      bodyLines,
      sourceLinks: (alert.links ?? []).slice(0, 3).map((url) => ({ label: "원문", url })),
      // 알림 대상 자산 중 첫 번째를 symbol 뱃지로 사용
      symbol: alert.assets?.[0]?.toUpperCase(),
    };
  });

  const ledgerExecutionByIntentId = new Map(
    (data.ledger?.recentExecutions ?? []).map((execution) => [execution.intentId, execution]),
  );

  const collabNoteItems = (data.collaborationNotes?.notes ?? []).map((note) => {
    const decision = note.verdict?.decision ?? "결정";
    const fullReason = sanitizeLine(note.verdict?.reason ?? "");

    // 예쁘고 가독성 좋게 bodyLines 구성 (줄바꿈 잘 되도록)
    const bodyLines: string[] = [];
    if (fullReason) bodyLines.push(fullReason);
    const execLine = collabExecutionLine(note, ledgerExecutionByIntentId);
    if (execLine) bodyLines.push(execLine);

    // Chips — 깔끔하고 의미 있게
    const chips: { label: string; tone: "role" | "status" | "execution" }[] = [
      { label: "협업", tone: "role" },
      { label: note.category.toUpperCase(), tone: "status" },
    ];

    const decisionTone = ["enter", "close", "reduce"].includes(decision.toLowerCase())
      ? "status"
      : decision.toLowerCase() === "withhold"
        ? "execution"
        : "status";

    chips.push({ label: decision.toUpperCase(), tone: decisionTone });

    const title = `협업 노트 · ${note.category.toUpperCase()} ${decision.toUpperCase()}`;

    const symbol = note.positionSnapshot?.symbol?.toUpperCase();

    return {
      id: `collab-${note.id}`,
      observedAt: note.closedAt ?? note.createdAt,
      speakerName: "협업 노트",
      speakerRole: "Collab",
      title,
      summary: fullReason || "협업 노트 결정 완료",
      bodyLines,
      chips,
      evidenceLine: undefined,
      targetNames: undefined,
      sourceLinks: undefined,
      symbol,
    };
  });

  return dedupeMeetingItems(
    [
      ...messageItems,
      ...alertItems,
      ...managementItems,
      ...reportItems,
      ...proposalItems,
      ...collabNoteItems,
    ].sort((left, right) => right.observedAt.localeCompare(left.observedAt)),
  ).slice(0, limit);
}
